import { useEffect, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { RealtimeEvent } from '@anidex/shared/types';
import { keys, useSession } from './queries';

export type RealtimeStatus = 'connecting' | 'open' | 'closed';

let status: RealtimeStatus = 'closed';
let socket: WebSocket | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let summaryPoll: ReturnType<typeof setInterval> | null = null;
let conversationPoll: ReturnType<typeof setInterval> | null = null;
let backoffMs = 1_000;
let intentionalClose = false;
let started = false;
let openConversationId: string | null = null;
let queryClient: QueryClient | null = null;

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function setStatus(next: RealtimeStatus) {
  if (status === next) return;
  status = next;
  emit();
}

function invalidateForEvent(event: RealtimeEvent) {
  if (!queryClient) return;
  switch (event.type) {
    case 'profile':
      void queryClient.invalidateQueries({ queryKey: keys.session });
      void queryClient.invalidateQueries({ queryKey: keys.me });
      break;
    case 'billing':
      for (const key of [keys.session, keys.me, keys.gacha, ['billing'], ['unlocks'], ['public_profile'], ['collection'], ['character'], ['trade_limits']]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      break;
    case 'summary':
      void queryClient.invalidateQueries({ queryKey: keys.summary });
      break;
    case 'notification':
      void queryClient.invalidateQueries({ queryKey: keys.notifications });
      void queryClient.invalidateQueries({ queryKey: keys.summary });
      break;
    case 'message':
      void queryClient.invalidateQueries({ queryKey: keys.messages(event.conversationId) });
      void queryClient.invalidateQueries({ queryKey: keys.conversations });
      void queryClient.invalidateQueries({ queryKey: keys.summary });
      break;
    case 'friend_request':
      void queryClient.invalidateQueries({ queryKey: keys.friendRequests });
      void queryClient.invalidateQueries({ queryKey: keys.friends });
      void queryClient.invalidateQueries({ queryKey: keys.summary });
      void queryClient.invalidateQueries({ queryKey: ['public_profile'] });
      break;
    case 'friend_update':
      void queryClient.invalidateQueries({ queryKey: keys.friends });
      void queryClient.invalidateQueries({ queryKey: keys.friendRequests });
      void queryClient.invalidateQueries({ queryKey: keys.friendActivity });
      void queryClient.invalidateQueries({ queryKey: keys.summary });
      void queryClient.invalidateQueries({ queryKey: ['public_profile'] });
      break;
    case 'wallet':
      void queryClient.invalidateQueries({ queryKey: keys.session });
      void queryClient.invalidateQueries({ queryKey: keys.me });
      void queryClient.invalidateQueries({ queryKey: keys.gacha });
      break;
    case 'market':
      void queryClient.invalidateQueries({ queryKey: ['market'] });
      void queryClient.invalidateQueries({ queryKey: ['auctions'] });
      void queryClient.invalidateQueries({ queryKey: ['auction'] });
      void queryClient.invalidateQueries({ queryKey: keys.marketMine });
      void queryClient.invalidateQueries({ queryKey: keys.auctionsMine });
      void queryClient.invalidateQueries({ queryKey: keys.marketHistory });
      void queryClient.invalidateQueries({ queryKey: ['market_sales'] });
      void queryClient.invalidateQueries({ queryKey: ['collection'] });
      void queryClient.invalidateQueries({ queryKey: keys.session });
      void queryClient.invalidateQueries({ queryKey: keys.me });
      break;
    case 'trade':
      void queryClient.invalidateQueries({ queryKey: ['trades'] });
      void queryClient.invalidateQueries({ queryKey: ['trade'] });
      void queryClient.invalidateQueries({ queryKey: ['partner_cards'] });
      void queryClient.invalidateQueries({ queryKey: keys.summary });
      void queryClient.invalidateQueries({ queryKey: ['collection'] });
      void queryClient.invalidateQueries({ queryKey: keys.session });
      void queryClient.invalidateQueries({ queryKey: keys.me });
      break;
    case 'collection':
      void queryClient.invalidateQueries({ queryKey: ['collection'] });
      void queryClient.invalidateQueries({ queryKey: keys.me });
      void queryClient.invalidateQueries({ queryKey: ['animes'] });
      void queryClient.invalidateQueries({ queryKey: ['anime'] });
      void queryClient.invalidateQueries({ queryKey: ['character'] });
      void queryClient.invalidateQueries({ queryKey: keys.session });
      break;
  }
}

function stopFallbackPolling() {
  if (summaryPoll) clearInterval(summaryPoll);
  if (conversationPoll) clearInterval(conversationPoll);
  summaryPoll = null;
  conversationPoll = null;
}

function invalidateLiveViews() {
  if (!queryClient) return;
  void queryClient.invalidateQueries({ queryKey: keys.session });
  void queryClient.invalidateQueries({ queryKey: keys.gacha });
  void queryClient.invalidateQueries({ queryKey: keys.summary });
  void queryClient.invalidateQueries({ queryKey: keys.friends });
  void queryClient.invalidateQueries({ queryKey: keys.friendRequests });
  void queryClient.invalidateQueries({ queryKey: ['market'] });
  void queryClient.invalidateQueries({ queryKey: ['auctions'] });
  void queryClient.invalidateQueries({ queryKey: keys.auctionsMine });
  void queryClient.invalidateQueries({ queryKey: ['auction'] });
  void queryClient.invalidateQueries({ queryKey: ['trades'] });
  void queryClient.invalidateQueries({ queryKey: ['trade'] });
  void queryClient.invalidateQueries({ queryKey: ['collection'] });
  void queryClient.invalidateQueries({ queryKey: keys.me });
}

function startFallbackPolling() {
  stopFallbackPolling();
  summaryPoll = setInterval(() => {
    invalidateLiveViews();
  }, 30_000);
  conversationPoll = setInterval(() => {
    if (!openConversationId) return;
    void queryClient?.invalidateQueries({ queryKey: keys.messages(openConversationId) });
  }, 15_000);
}

function clearReconnect() {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
}

function clearPing() {
  if (pingTimer) clearInterval(pingTimer);
  pingTimer = null;
}

function connect() {
  if (!started || intentionalClose) return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;

  setStatus('connecting');
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const ws = new WebSocket(`${protocol}//${location.host}/api/realtime`);
  socket = ws;

  ws.addEventListener('open', () => {
    backoffMs = 1_000;
    setStatus('open');
    invalidateLiveViews();
    stopFallbackPolling();
    clearPing();
    pingTimer = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 30_000);
  });

  ws.addEventListener('message', (event) => {
    if (typeof event.data !== 'string' || event.data === 'pong') return;
    try {
      invalidateForEvent(JSON.parse(event.data) as RealtimeEvent);
    } catch {
      // Ignore non-event payloads.
    }
  });

  ws.addEventListener('close', () => {
    socket = null;
    clearPing();
    setStatus('closed');
    startFallbackPolling();
    if (intentionalClose || !started) return;
    clearReconnect();
    reconnectTimer = setTimeout(() => {
      backoffMs = Math.min(backoffMs * 2, 30_000);
      connect();
    }, backoffMs);
  });

  ws.addEventListener('error', () => {
    ws.close();
  });
}

function wakeSocket() {
  if (!started || intentionalClose) return;
  if (status === 'open' || status === 'connecting') return;
  backoffMs = 1_000;
  clearReconnect();
  connect();
}

function onOnline() {
  wakeSocket();
}

function onVisibility() {
  if (document.visibilityState !== 'visible') return;
  wakeSocket();
  invalidateLiveViews();
}

export function startRealtime(client: QueryClient) {
  queryClient = client;
  if (started) return;
  started = true;
  intentionalClose = false;
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibility);
  connect();
}

export function stopRealtime() {
  intentionalClose = true;
  started = false;
  clearReconnect();
  clearPing();
  stopFallbackPolling();
  window.removeEventListener('online', onOnline);
  document.removeEventListener('visibilitychange', onVisibility);
  socket?.close();
  socket = null;
  setStatus('closed');
}


export function setOpenConversation(conversationId: string | null) {
  openConversationId = conversationId;
}

export function useRealtimeStatus() {
  return useSyncExternalStore(
    (onStoreChange) => {
      listeners.add(onStoreChange);
      return () => listeners.delete(onStoreChange);
    },
    () => status,
    () => 'closed' as RealtimeStatus,
  );
}


export function useRealtime() {
  const client = useQueryClient();
  const userId = useSession().data?.user?.id;
  useEffect(() => {
    if (!userId) {
      stopRealtime();
      return;
    }
    startRealtime(client);
    return () => stopRealtime();
  }, [client, userId]);
  return useRealtimeStatus();
}
